// @ts-nocheck
import { useQueryClient } from '@tanstack/react-query';
import { WorkflowService } from './workflowService';
import {
  useMutationEngine,
  useNewQueryEngine,
} from '../../../../queries/useDataEngine';
import { EMPTY_OBJECT } from '../../../../const/CommonConst';

const WORKFLOW = 'workflow';
export const WORKFLOW_QUERY_KEYS = {
  ALL: [WORKFLOW],
  workflowList: {
    ROOT: [WORKFLOW, 'workflowList'],
    key: (params = {}) => [WORKFLOW, 'workflowList', params],
  },
  singleWorkflow: {
    ROOT: [WORKFLOW, 'singleWorkflow'],
    key: (params = {}) => [WORKFLOW, 'singleWorkflow', params],
  },
  draftWorkflow: {
    ROOT: [WORKFLOW, 'draftWorkflow'],
    key: (params = {}) => [WORKFLOW, 'draftWorkflow', params],
  },
  allStates: {
    ROOT: [WORKFLOW, 'allStates'],
    key: (params = {}) => [WORKFLOW, 'allStates', params],
  },
  nextPossibleState: {
    ROOT: [WORKFLOW, 'nextPossibleState'],
    key: (params = {}) => [WORKFLOW, 'nextPossibleState', params],
  },
  precheckTransition: {
    ROOT: [WORKFLOW, 'precheckTransition'],
    key: (params = {}) => [WORKFLOW, 'precheckTransition', params],
  },
  workflowGroups: {
    ROOT: [WORKFLOW, 'workflowGroups'],
    key: (params = {}) => [WORKFLOW, 'workflowGroups', params],
  },
};

export const useWorkflowListQuery = ({
  params = EMPTY_OBJECT,
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useNewQueryEngine(
    WORKFLOW_QUERY_KEYS.workflowList.key,
    WorkflowService.getWorkflowList,
    params,
    options
  );
};

export const useSingleWorkflowQuery = ({
  params = EMPTY_OBJECT,
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useNewQueryEngine(
    WORKFLOW_QUERY_KEYS.singleWorkflow.key,
    WorkflowService.getSingleWorkflow,
    params,
    { loader: false, ...options }
  );
};

export const useDraftWorkflowQuery = ({
  params = EMPTY_OBJECT,
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useNewQueryEngine(
    WORKFLOW_QUERY_KEYS.draftWorkflow.key,
    WorkflowService.getDraftWorkflow,
    params,
    options
  );
};

export const useAllStatesQuery = ({
  params = EMPTY_OBJECT,
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useNewQueryEngine(
    WORKFLOW_QUERY_KEYS.allStates.key,
    WorkflowService.fetchAllStates,
    params,
    options
  );
};

export const useNextPossibleStateQuery = ({
  params,
  options,
} = EMPTY_OBJECT) => {
  return useNewQueryEngine(
    WORKFLOW_QUERY_KEYS.nextPossibleState.key,
    WorkflowService.getNextPossibleState,
    params,
    { loader: false, ...options }
  );
};

export const usePrecheckTransitionQuery = ({
  params = EMPTY_OBJECT,
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useNewQueryEngine(
    WORKFLOW_QUERY_KEYS.precheckTransition.key,
    WorkflowService.getPrecheckTransition,
    params,
    { loader: false, ...options }
  );
};

export const useWorkflowGroupsQuery = ({
  params = EMPTY_OBJECT,
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useNewQueryEngine(
    WORKFLOW_QUERY_KEYS.workflowGroups.key,
    WorkflowService.getWorkflowGroups,
    params,
    { loader: false, ...options }
  );
};

export const useCreateWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useMutationEngine(WorkflowService.createWorkflow, {
    ...options,
    invalidateQueries: [WORKFLOW_QUERY_KEYS.workflowList.ROOT],
  });
};

export const useCloneWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useMutationEngine(WorkflowService.cloneWorkflow, {
    ...options,
    invalidateQueries: [WORKFLOW_QUERY_KEYS.workflowList.ROOT],
  });
};

export const useGenerateTextToWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useMutationEngine(WorkflowService.generateTextToWorkflow, {
    ...options,
    invalidateQueries: [WORKFLOW_QUERY_KEYS.workflowList.ROOT],
  });
};

export const useCompileWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) =>
  useMutationEngine(WorkflowService.compileWorkflow, options);

export const useUpdateWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  const queryClient = useQueryClient();
  return useMutationEngine(WorkflowService.updateWorkflow, {
    ...options,
    invalidateQueries: [
      WORKFLOW_QUERY_KEYS.workflowGroups.ROOT,
      WORKFLOW_QUERY_KEYS.workflowList.ROOT,
      WORKFLOW_QUERY_KEYS.singleWorkflow.ROOT,
    ],
    onSuccess: (...args) => {
      // clear the draftWorkflow cache
      queryClient.removeQueries({
        queryKey: WORKFLOW_QUERY_KEYS.draftWorkflow.ROOT,
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useCheckWorkflowStateDeletableMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) =>
  useMutationEngine(WorkflowService.checkWorkflowStateDeletable, options);

export const useSaveDraftWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  const queryClient = useQueryClient();
  return useMutationEngine(WorkflowService.saveDraftWorkflow, {
    ...options,
    onSuccess: (...args) => {
      // clear the draftWorkflow cache
      // clear the draftWorkflow cache
      queryClient.removeQueries({
        queryKey: WORKFLOW_QUERY_KEYS.draftWorkflow.ROOT,
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateDraftWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  const queryClient = useQueryClient();
  return useMutationEngine(WorkflowService.updateDraftWorkflow, {
    ...options,
    invalidateQueries: [WORKFLOW_QUERY_KEYS.workflowList.ROOT],
    onSuccess: (...args) => {
      // clear the draftWorkflow cache
      // clear the draftWorkflow cache
      queryClient.removeQueries({
        queryKey: WORKFLOW_QUERY_KEYS.draftWorkflow.ROOT,
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteDraftWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  const queryClient = useQueryClient();
  return useMutationEngine(WorkflowService.deleteDraftWorkflow, {
    ...options,
    onSuccess: (...args) => {
      // clear the draftWorkflow cache
      // clear the draftWorkflow cache
      queryClient.removeQueries({
        queryKey: WORKFLOW_QUERY_KEYS.draftWorkflow.ROOT,
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteWorkflowMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useMutationEngine(WorkflowService.deleteWorkflow, {
    ...options,
    invalidateQueries: [WORKFLOW_QUERY_KEYS.workflowList.ROOT],
  });
};

export const useUpdateWorkflowStateMutation = ({
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useMutationEngine(WorkflowService.updateWorkflowState, {
    ...options,
    invalidateQueries: [
      WORKFLOW_QUERY_KEYS.singleWorkflow.ROOT,
      WORKFLOW_QUERY_KEYS.workflowList.ROOT,
    ],
  });
};
